import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Button, Select, DatePicker } from "antd";
import {
  ExpandOutlined,
  EditOutlined,
  ShareAltOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTags, useUserManagement } from "hooks";
import RichTextEditor from "components/common/RichTextEditor";
import { useAuth } from "contexts/AuthContext";
import { SimpleTagManager } from "./SimpleTagManager";
import useUsers from "hooks/userManagement/useUsers";

const { TextArea } = Input;

/* ----- Enums (mirror backend) ----- */
const categories = [
  { value: "TASK", label: "Task" },
  { value: "FEATURE_REQUEST", label: "Feature Request" },
  { value: "IMPROVEMENT", label: "Improvement" },
  { value: "BUG", label: "Bug" },
];

const taskStatuses = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "BLOCKED", label: "Blocked" },
];

const assignmentStatuses = [
  { value: "ACCEPTED", label: "Accepted" },
  { value: "OTW", label: "On The Way" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "EXPIRED", label: "Expired" },
];

const priorities = [
  { value: 0, label: "Critcal" },
  { value: 1, label: "High" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Low" },
];

const storyPoints = [1, 2, 3, 5, 8, 13].map((p) => ({
  value: p,
  label: p,
}));

/* ----- component ----- */
const TaskFormDrawer = ({
  open,
  onClose,
  onSubmit,
  mode = "create",
  initialValues = null,
  // projectMembers,
  loading,
}) => {
  const { user } = useAuth();
  const { firstName, lastName } = user || {};
  const fullName = `${firstName} ${lastName}`;
  const { users, loading: usersLoading } = useUsers(0, 100, true);
  const { tags, loading: tagsLoading } = useTags();

  const [isExpanded, setIsExpanded] = useState(false);
  const [localTags, setLocalTags] = useState(initialValues?.tags || []);
  const [form] = Form.useForm();


  useEffect(() => {
    const creatorName =
      `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
      user?.username ||
      user?.email ||
      "";

    if (mode === "edit" && initialValues) {
      form.setFieldsValue({
        ...initialValues,
        startedAt: initialValues.startedAt
          ? dayjs(initialValues.startedAt)
          : null,
        expectedCompletionDate: initialValues.expectedCompletionDate
          ? dayjs(initialValues.expectedCompletionDate)
          : null,
        actualCompletionDate: initialValues.actualCompletionDate
          ? dayjs(initialValues.actualCompletionDate)
          : null,
        tags: initialValues.tags?.map((tag) => tag.id) || [],
      });
      setLocalTags(initialValues.tags || []);
    } else if (mode === "create") {
      form.resetFields();
      form.setFieldsValue({
        status: "OPEN",
        priority: 2,
        points: 1,
        createdByName: creatorName || undefined,
      });
    }
  }, [
    initialValues,
    mode,
    form,
    user?.firstName,
    user?.lastName,
    user?.username,
    user?.email,
  ]);

  const handleFinish = async (values) => {
    const payload = {
      type: values.type,
      title: values.title,
      description: values.description,
      acceptanceCriteria: values.acceptanceCriteria,
      status: values.status,
      priority: values.priority,
      points: values.points,
      tagIds: values.tags || [],
      assigneeName: values.assigneeName || null,
      createdByName: fullName,
      assignmentStatus: values.assignmentStatus,
      startedAt: values.startedAt?.toISOString(),
      expectedCompletionDate: values.expectedCompletionDate?.toISOString(),
      actualCompletionDate: values.actualCompletionDate?.toISOString(),
    };

    await onSubmit?.(payload);

    if (mode === "create") {
      form.resetFields();
    }
  };

  return (
    <Drawer
      size={isExpanded ? 520 : "100vw"}
      zIndex={9999}
      open={open}
      onClose={onClose}
      destroyOnHidden
      closable={false}
      footer={
        <div
          style={{
            textAlign: "right",
            maxWidth: isExpanded ? "100vw" : 700,
            margin: isExpanded ? 0 : "0 auto",
            paddingTop: 10,
            paddingBottom: 10,
          }}
        >
          <Button style={{ marginRight: 8 }} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            {mode === "edit" ? "Save Changes" : "Create"}
          </Button>
        </div>
      }
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, san-serif",
        }}
      >
        {/* Breadcrumb / page context */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            <span style={{ cursor: "pointer" }}>Craftboard Project</span>
            <span style={{ margin: "0 6px" }}>/</span>
            <span style={{ color: "#111827", fontWeight: 500 }}>
              {mode === "edit" ? "Edit Task" : "Create Task"}
            </span>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <ShareAltOutlined className="task-drawer-icon" />
            <MoreOutlined className="task-drawer-icon" />
          </div>
        </div>

        <Form layout="vertical" form={form} onFinish={handleFinish}>
          {/* category */}
          <Form.Item name="type" label="Category" rules={[{ required: true }]}>
            <Select
              options={categories}
              allowClear
              placeholder="Select category"
            />
          </Form.Item>

          {/* title */}
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="Task title" />
          </Form.Item>

          {/* description */}
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Description is required" }]}
            style={{ marginBottom: 10 }}
          >
            <RichTextEditor
              height="150px"
              placeholder="Write task description..."
            />
          </Form.Item>

          {/* Acceptance Criteria */}
          <Form.Item
            name="acceptanceCriteria"
            label="Acceptance Criteria"
            rules={[
              { required: true, message: "Acceptance criteria is required" },
            ]}
            style={{ marginBottom: 10 }}
          >
            <RichTextEditor
              height="150px"
              placeholder="Write the acceptance criteria..."
            />
          </Form.Item>

          {/* Status */}
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={taskStatuses} placeholder="Status" />
          </Form.Item>

          {/* Assignment Status */}
          <Form.Item name="assignmentStatus" label="Assignment Status">
            <Select
              options={assignmentStatuses}
              allowClear
              placeholder="Assignment status"
            />
          </Form.Item>

          {/* Assignee */}
          <Form.Item
            name="assigneeName"
            label="Assignee"
            rules={[{ required: true }]}
          >
            <Select
              allowClear
              placeholder="Search and select assignee"
              loading={usersLoading}
              showSearch={{
                optionFilterProp: "label",
                filterOption: (input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase()),
              }}
              options={users.map((user) => ({
                value: `${user.firstName} ${user.lastName}`,
                label: `${user.firstName} ${user.lastName} (${user.email})`,
              }))}
            />
          </Form.Item>

          {/* Priority */}
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true }]}
          >
            <Select options={priorities} placeholder="Select priority" />
          </Form.Item>

          {/* Story Points */}
          <Form.Item name="points" label="Points" rules={[{ required: true }]}>
            <Select options={storyPoints} placeholder="Select points" />
          </Form.Item>

          {/* Dates */}
          <Form.Item
            name="startedAt"
            label="Started At"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            name="expectedCompletionDate"
            label="Expected Completion Date"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item name="actualCompletionDate" label="Actual Completion Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          {/* Tags */}
          {mode === "edit" && (
            <>
              <label style={{ color: "#555" }}>Tags</label>
              <SimpleTagManager
                entityType="task"
                entityId={initialValues?.id}
                projectId={initialValues?.projectId}
                currentTags={localTags}
                availableTags={tags}
                onChange={(updatedTags) => {
                  setLocalTags(updatedTags);
                }}
              />
            </>
          )}
        </Form>
      </div>
    </Drawer>
  );
};

export default TaskFormDrawer;
